import { memo, useMemo, Fragment, useState } from "react";
import type { FooterProps } from "../../../smb-ui-welcome/src/types/components/core.type";
import parse from "html-react-parser";
import YourPrivacyChoicesComponent from '../components/your-privacy-choices';
import { PrivacyChoicesIcon } from "../components/SMBIcons";
// Update the import path below to the correct relative path if needed
import { trackEventOnClick } from "../utils/reportingData";

const Footer = memo(({
    aemContent = {
        footerBody: "",
        emailFooter: "",
        copyRight: "",
        businessAddress: "",
        footerLinks: []
    }
}: FooterProps) => {
    const [showPrivacyModal, setShowPrivacyModal] = useState(false);
    const { footerBody, emailFooter, copyRight, businessAddress, footerLinks } = aemContent || {};
    const hasLinks = footerLinks && footerLinks.length > 0;

    const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, label: string) => {
        if (label?.includes('Your California privacy choices')) {
            e.preventDefault();
            setShowPrivacyModal(true);
            return;
        }
        trackEventOnClick({
            actionName: `${label.toLowerCase()}`,
            subSiteSection: label.toLowerCase()
        });
    }

    // Memoize parsed content to avoid re-parsing on every render
    const parsedFooterBody = useMemo(() => parse(footerBody), [footerBody]);
    const parsedEmailFooter = useMemo(() => parse(emailFooter), [emailFooter]);
    const parsedBusinessAddress = useMemo(() => parse(businessAddress), [businessAddress]);

    return (
        <footer data-testid='footer-section' className="w-full h-88 py-4 max-sm:h-96 bg-[rgba(241,245,251,1)] sm:py-10 lg:h-64">
            <div className="w-full text-[#282828] flex flex-col h-full gap-8 px-4 lg:px-[6.25%]">
                <div className="flex flex-1 flex-col lg:flex-row gap-8 lg:gap-12">
                    <div className="text-base leading-relaxed [&>p:first-child]:font-medium" data-testid='footer-body-section'>
                        {parsedFooterBody}
                    </div>
                    <div className="text-base leading-relaxed [&>p:first-child]:font-medium" data-testid='footer-email-section'>
                        {parsedEmailFooter}
                    </div>
                </div>
                <div className="flex lg:flex-row flex-col justify-between gap-5 text-[10px] font-normal">
                    <div className="order-2 lg:order-1">
                        {copyRight && <p>{copyRight}</p>}
                        {businessAddress && <p>{parsedBusinessAddress}</p>}
                    </div>
                    {hasLinks && (
                        <div className="flex flex-wrap h-auto gap-1 space-x-2 sm:h-4 order-1 lg:order-2">
                            {footerLinks.map((item, index) => (
                                <Fragment key={`footer-link-${index}-${item?.label}`}>
                                    <a
                                        id={item?.id || ''}
                                        href={item?.url}
                                        target={item?.openNewTab ? "_blank" : "_self"}
                                        rel="noopener noreferrer"
                                        className="flex gap-0.5 [&>img]:w-auto [&>img]:h-[14px] whitespace-nowrap"
                                        onClick={(e) => { handleClick(e, item?.label) }}
                                    >
                                        {parse(item?.label)}
                                        <span className="inline-flex items-center">
                                            {item?.label.includes('Your California privacy choices') ?
                                                <PrivacyChoicesIcon className="inline-block h-4 w-8 ml-1.5" /> : null}
                                        </span>
                                    </a>
                                    {index < footerLinks?.length - 1 && <div className="border-r border-gray-800" />}
                                </Fragment>
                            ))}
                        </div>
                    )}
                </div>
            </div>
            <YourPrivacyChoicesComponent isOpen={showPrivacyModal} onClose={() => setShowPrivacyModal(false)} />

        </footer>
    );
});

export default Footer;